import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Clock, Store, Trash2, AlertTriangle, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface HorarioEspecial {
  id: string;
  data: string;
  tipo: "horario_especial" | "fechado";
  mensagem: string | null;
  horario_abertura: string | null;
  horario_fechamento: string | null;
}

export function HorariosEspeciais() {
  const [horarios, setHorarios] = useState<HorarioEspecial[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tipo, setTipo] = useState<"horario_especial" | "fechado">("fechado");
  const [mensagem, setMensagem] = useState("");
  const [horarioAbertura, setHorarioAbertura] = useState("09:00");
  const [horarioFechamento, setHorarioFechamento] = useState("18:00");
  const [existingConfigId, setExistingConfigId] = useState<string | null>(null);

  // Effect to load data if selected date already has configuration
  // Only auto-sync when the date changes or the listing changes
  useEffect(() => {
    if (date) {
      const dateStr = format(date, "yyyy-MM-dd");
      const existing = horarios.find(h => h.data === dateStr);
      
      if (existing) {
        setExistingConfigId(existing.id);
        // Only auto-switch the type if we found an existing config and we aren't already in an explicit mode
        // Or simpler: sync everything ONLY when the date itself changes.
      } else {
        setExistingConfigId(null);
      }
    }
  }, [date, horarios]);

  // Handle syncing fields when date changes SPECIFICALLY
  const [prevDate, setPrevDate] = useState<string | null>(null);
  useEffect(() => {
    if (date) {
      const dateStr = format(date, "yyyy-MM-dd");
      if (dateStr !== prevDate) {
        const existing = horarios.find(h => h.data === dateStr);
        if (existing) {
          setTipo(existing.tipo);
          setMensagem(existing.mensagem || "");
          setHorarioAbertura(existing.horario_abertura?.slice(0, 5) || "09:00");
          setHorarioFechamento(existing.horario_fechamento?.slice(0, 5) || "18:00");
        } else {
          setMensagem("");
          // Keep the current selection of 'tipo' as it might be what the user wants to set
          setHorarioAbertura("09:00");
          setHorarioFechamento("18:00");
        }
        setPrevDate(dateStr);
      }
    }
  }, [date, horarios, prevDate]);

  // Default to today if closed mode is selected and no date is set
  useEffect(() => {
    if (tipo === "fechado" && !date) {
      setDate(new Date());
    }
  }, [tipo, date]);

  useEffect(() => {
    fetchHorarios();
  }, []);

  async function fetchHorarios() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("horarios_especiais")
        .select("*")
        .order("data", { ascending: true });
      
      if (error) throw error;
      setHorarios((data || []) as HorarioEspecial[]);
    } catch (error) {
      console.error("Erro ao carregar horários especiais:", error);
      toast.error("Erro ao carregar horários especiais");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    // Se for fechado, garantimos uma data (hoje se não selecionada)
    let targetDate = date;
    if (tipo === "fechado" && !targetDate) {
      targetDate = new Date();
    }

    if (!targetDate) {
      toast.warning("Selecione uma data");
      return;
    }

    const dateStr = format(targetDate, "yyyy-MM-dd");
    setSaving(true);
    
    try {
      const payload: any = {
        data: dateStr,
        tipo,
        mensagem: mensagem || null
      };

      if (tipo === "horario_especial") {
        payload.horario_abertura = horarioAbertura;
        payload.horario_fechamento = horarioFechamento;
      } else {
        payload.horario_abertura = null;
        payload.horario_fechamento = null;
      }

      // Verificar se já existe config para esta data mas não detectamos no form 
      // (caso o usuário mude a data e não dispare o useEffect a tempo)
      const { data: checkExisting } = await supabase
        .from("horarios_especiais")
        .select("id")
        .eq("data", dateStr)
        .maybeSingle();

      if (checkExisting || existingConfigId) {
        // Atualizar
        const { error } = await supabase
          .from("horarios_especiais")
          .update(payload)
          .eq("data", dateStr);
        if (error) throw error;
        toast.success("Configuração atualizada com sucesso!");
      } else {
        // Inserir
        const { error } = await supabase
          .from("horarios_especiais")
          .insert(payload);
        if (error) throw error;
        toast.success(tipo === "fechado" 
          ? "Loja Fechada com sucesso!" 
          : "Horário especial salvo com sucesso!");
      }
      
      fetchHorarios();
    } catch (error: any) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar: " + (error?.message || "Tente novamente"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      const { error } = await supabase
        .from("horarios_especiais")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast.success("Configuração removida com sucesso!");
      
      setHorarios(prev => prev.filter(h => h.id !== id));
      
      if (existingConfigId === id) {
        setExistingConfigId(null);
        setMensagem("");
        // We don't force a mode switch here to stay on the user's intent
      }
      // Re-fetch in background to ensure sync, but the local state update above handles the "immediate" removal
      fetchHorarios();
    } catch (error: any) {
      console.error("Erro ao deletar:", error);
      toast.error("Erro ao remover: " + (error?.message || "Tente novamente"));
    }
  }

  return (
    <Card className="border-warning/30 bg-zinc-900/20 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-warning uppercase font-black tracking-tighter">
          <Clock className="h-6 w-6" />
          Configurações de Abertura
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Seletor de Modo: Fechar Loja vs Horário Especial */}
        <div className="grid grid-cols-2 gap-6">
          <button
            onClick={() => {
              setTipo("fechado");
              if (!date) setDate(new Date());
            }}
            className={cn(
              "flex flex-col items-center gap-4 p-8 rounded-[2rem] border-2 transition-all duration-500",
              tipo === "fechado" 
                ? "bg-red-500/10 border-red-500 shadow-[0_0_40px_rgba(239,68,68,0.3)] scale-105 z-10" 
                : "bg-zinc-900/40 border-white/5 hover:border-red-500/30 opacity-60 hover:opacity-100"
            )}
          >
            <div className={cn(
              "p-5 rounded-full border-2 transition-all",
              tipo === "fechado" ? "bg-red-500/20 border-red-500 text-red-500" : "bg-zinc-800 border-white/10 text-muted-foreground"
            )}>
              <Store className="h-10 w-10" />
            </div>
            <div className="text-center">
              <p className={cn("font-black text-2xl uppercase tracking-tighter", tipo === "fechado" ? "text-red-500" : "text-slate-400")}>Fechar Loja</p>
              <p className="text-xs font-bold text-muted-foreground mt-1 uppercase opacity-50 tracking-widest">Ação Imediata (Hoje)</p>
            </div>
          </button>

          <button
            onClick={() => setTipo("horario_especial")}
            className={cn(
              "flex flex-col items-center gap-4 p-8 rounded-[2rem] border-2 transition-all duration-500",
              tipo === "horario_especial" 
                ? "bg-warning/10 border-warning shadow-[0_0_40px_rgba(245,158,11,0.3)] scale-105 z-10" 
                : "bg-zinc-900/40 border-white/5 hover:border-warning/30 opacity-60 hover:opacity-100"
            )}
          >
            <div className={cn(
              "p-5 rounded-full border-2 transition-all",
              tipo === "horario_especial" ? "bg-warning/20 border-warning text-warning" : "bg-zinc-800 border-white/10 text-muted-foreground"
            )}>
              <Clock className="h-10 w-10" />
            </div>
            <div className="text-center">
              <p className={cn("font-black text-2xl uppercase tracking-tighter", tipo === "horario_especial" ? "text-warning" : "text-slate-400")}>Horário Especial</p>
              <p className="text-xs font-bold text-muted-foreground mt-1 uppercase opacity-50 tracking-widest">Configuração Programada</p>
            </div>
          </button>
        </div>

        {/* Formulário */}
        <div className="p-8 rounded-[2.5rem] bg-zinc-950/60 border border-white/5 space-y-8 relative overflow-hidden shadow-inner">
          <div className={cn("absolute top-0 left-0 w-full h-2", tipo === "fechado" ? "bg-red-500" : "bg-warning")} />
          
          <div className="grid gap-8 md:grid-cols-2">
            {/* Data (Opcional no modo Fechado, assume hoje) */}
            {tipo === "horario_especial" && (
              <div className="space-y-3">
                <Label className="text-sm font-black uppercase text-slate-500 tracking-widest ml-1">Data da Ocorrência</Label>
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "w-full h-16 justify-start text-left font-black border-white/10 bg-zinc-900 rounded-2xl text-lg",
                      !date && "text-muted-foreground",
                      date && (tipo === "fechado" ? "border-red-500/50 text-red-500" : "border-warning/50 text-warning")
                    )}
                  >
                    <CalendarIcon className="mr-3 h-6 w-6" />
                    {date ? format(date, "PPP", { locale: ptBR }) : "SELECIONE A DATA"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-white/10 shadow-2xl bg-zinc-900" align="start">
                  <Calendar 
                    mode="single" 
                    selected={date} 
                    onSelect={newDate => {
                      setDate(newDate);
                      setIsDatePickerOpen(false);
                    }} 
                    initialFocus 
                    className="bg-zinc-900"
                    disabled={(selectedDate) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const target = new Date(selectedDate);
                      target.setHours(0, 0, 0, 0);
                      return target < today;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            )}

            {/* Horários / Mensagem */}
            {tipo === "horario_especial" ? (
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="space-y-3">
                  <Label className="text-xs sm:text-sm font-black uppercase text-slate-500 tracking-widest ml-1">Abertura</Label>
                  <Input
                    type="time"
                    value={horarioAbertura}
                    onChange={(e) => setHorarioAbertura(e.target.value)}
                    className="h-16 bg-zinc-900 border-white/10 font-black text-sm sm:text-xl rounded-xl sm:rounded-2xl px-2 sm:px-4"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-xs sm:text-sm font-black uppercase text-slate-500 tracking-widest ml-1">Fechamento</Label>
                  <Input
                    type="time"
                    value={horarioFechamento}
                    onChange={(e) => setHorarioFechamento(e.target.value)}
                    className="h-16 bg-zinc-900 border-white/10 font-black text-sm sm:text-xl rounded-xl sm:rounded-2xl px-2 sm:px-4"
                  />
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-2xl border border-red-500/10 bg-red-500/5 flex items-center gap-4 md:col-span-2">
                <div className="p-3 bg-red-500/10 rounded-xl">
                  <Store className="h-8 w-8 text-red-500 animate-pulse" />
                </div>
                <div>
                  <p className="text-xs font-black text-red-500 uppercase tracking-widest">Modo Emergência</p>
                  <p className="text-sm font-bold text-slate-400 leading-tight">Agendamentos serão bloqueados para hoje.</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-black uppercase text-slate-500 tracking-widest ml-1">Mensagem para os Clientes</Label>
            <Input
              placeholder={tipo === "fechado" ? "Ex: Hoje não abriremos devido a treinamento interno..." : "Ex: Horário especial para o feriado de Natal..."}
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              className="h-16 bg-zinc-900 border-white/10 rounded-2xl text-lg font-bold"
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-white/5">
            {existingConfigId ? (
              <Button 
                onClick={() => handleDelete(existingConfigId)}
                variant="outline"
                className={cn(
                  "h-16 px-10 font-black rounded-2xl transition-all uppercase tracking-widest w-full sm:w-auto overflow-hidden relative group",
                  tipo === "fechado" 
                    ? "border-green-500/30 text-green-500 hover:bg-green-500/10 bg-green-500/5 shadow-[0_0_20px_rgba(34,197,94,0.1)] hover:shadow-[0_0_30px_rgba(34,197,94,0.3)]" 
                    : "border-red-500/30 text-red-500 hover:bg-red-500/10"
                )}
              >
                {tipo === "fechado" ? (
                  <>
                    <div className="absolute inset-0 bg-green-500/5 transition-transform group-hover:scale-110" />
                    <CheckCircle2 className="mr-3 h-6 w-6 relative z-10" />
                    <span className="relative z-10 text-base sm:text-xl tracking-tighter sm:tracking-widest">REABRIR LOJA AGORA</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-3 h-6 w-6" />
                    <span className="text-base sm:text-xl tracking-tighter sm:tracking-widest">Remover Configuração</span>
                  </>
                )}
              </Button>
            ) : <div />}
            
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className={cn(
                "px-8 sm:px-16 h-18 text-xl sm:text-2xl font-black rounded-2xl transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.5)] active:scale-95 uppercase tracking-tight w-full sm:w-auto",
                tipo === "fechado" ? "bg-red-600 text-white hover:bg-red-500" : "bg-warning text-white hover:bg-warning/90"
              )}
            >
              {saving ? <><Loader2 className="mr-3 h-8 w-8 animate-spin" />SALVANDO...</> : (existingConfigId ? "ATUALIZAR" : (tipo === "fechado" ? "FECHAR LOJA AGORA" : "SALVAR HORÁRIO"))}
            </Button>
          </div>
        </div>

        {/* Lista de Ativos */}
        {horarios.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-black text-xl uppercase tracking-tighter text-slate-400 ml-1">Configurações Ativas</h3>
            <div className="grid gap-4">
              {horarios.map((h) => (
                <div 
                  key={h.id} 
                  className={cn(
                    "flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 rounded-3xl border transition-all",
                    h.tipo === "fechado" 
                      ? "bg-red-500/5 border-red-500/20 hover:border-red-500/40" 
                      : "bg-warning/5 border-warning/20 hover:border-warning/40"
                  )}
                >
                  <div className="flex items-center gap-5 mb-4 sm:mb-0">
                    <div className={cn("p-4 rounded-2xl", h.tipo === "fechado" ? "bg-red-500/10 text-red-500" : "bg-warning/10 text-warning")}>
                      {h.tipo === "fechado" ? <Store className="h-8 w-8" /> : <Clock className="h-8 w-8" />}
                    </div>
                    <div>
                      <p className="text-lg sm:text-2xl font-black text-white uppercase tracking-tighter">
                        {format(new Date(h.data + 'T12:00:00'), "dd 'de' MMMM", { locale: ptBR })}
                      </p>
                      <p className="text-[10px] sm:text-sm font-bold text-slate-500 uppercase tracking-normal sm:tracking-widest">
                        {h.tipo === "fechado" 
                          ? "Loja Fechada" 
                          : `Aberto: ${h.horario_abertura?.slice(0, 5)} - ${h.horario_fechamento?.slice(0, 5)}`}
                        {h.mensagem && ` • ${h.mensagem}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(h.id)}
                    className="h-14 w-14 rounded-2xl text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    <Trash2 className="h-8 w-8" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
